import { login as loginSdk } from "@hops/sdk";
import { useCallback, useState } from "react";
import { type AuthUser, useAuthStore } from "../../auth/auth-store";

export function useAuth() {
  const { isAuthenticated, user, token, clearAuth } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const login = useCallback(async (username: string, password: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error: apiError } = await loginSdk({
        body: { username, password },
      });

      if (apiError || !data) {
        setError("Invalid username or password");
        return false;
      }

      const authUser: AuthUser = {
        id: Number(data.user.id),
        username: data.user.username,
        division: data.user.division,
        divisionName: data.user.divisionName,
        accessGroup: data.user.accessGroup,
        salesRep: data.user.salesRep ?? null,
      };

      // The login endpoint returns { token, user } — refreshToken comes
      // from the body-based refresh endpoint. Store empty string for now;
      // the first refresh call will populate it.
      const refreshToken = (data as Record<string, unknown>).refreshToken as
        | string
        | undefined;

      useAuthStore.getState().setAuth(data.token, refreshToken ?? "", authUser);

      return true;
    } catch {
      setError("Network error — check your connection");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    clearAuth();
  }, [clearAuth]);

  return {
    isAuthenticated,
    user,
    token,
    isLoading,
    error,
    login,
    logout,
  };
}
